import { Mail, UserCheck, UserPlus, UserX } from 'lucide-react';
import * as React from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@payment-plan/ui';
import { AdminFormDialog } from './AdminFormDialog';
import { useAdmins, useResendAdminInvitation, useToggleAdmin } from './use-admins';
import type { AdminUser } from '../../../types/admin';
import { textoSegunAcceso, useAccesoAdmin } from '../acceso-admin';
import { InmobiliariaSelector } from '../inmobiliaria-activa/inmobiliaria-activa';

/**
 * fix-60 · S-01 (ratificado por el dev): administradores de la inmobiliaria. El superadmin los da de
 * alta por invitación por correo (mismo patrón que Asesores, Web 03). El PIN de confirmación de la
 * invitación queda para feat-63.
 */
export function UsersPage(): React.JSX.Element {
  const { data, isLoading, isError, refetch } = useAdmins();
  const reenviar = useResendAdminInvitation();
  const toggle = useToggleAdmin();
  const [formOpen, setFormOpen] = React.useState(false);
  const [toToggle, setToToggle] = React.useState<AdminUser | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);

  const acceso = useAccesoAdmin('usuario');
  const puedeCrear = acceso.crear;
  const puedeEditar = acceso.editar;
  const lista = data ?? [];

  return (
    <TooltipProvider delayDuration={200}>
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <PageHeader
          eyebrow="Administración"
          title="Administradores"
          description={textoSegunAcceso(acceso, {
            gestion: 'Invita a los administradores de la inmobiliaria: consultan proyectos, catálogo y separaciones.',
            consulta: 'Consulta los administradores de tu inmobiliaria.',
          })}
          action={
            puedeCrear ? (
              <Button className="gap-2" onClick={() => setFormOpen(true)}>
                <UserPlus className="h-4 w-4" />
                Nuevo administrador
              </Button>
            ) : undefined
          }
        />

        <InmobiliariaSelector />

        {feedback && (
          <p role="status" className="rounded-lg border border-border/70 bg-surface-fade px-4 py-3 text-sm">
            {feedback}
          </p>
        )}

        {isLoading ? (
          <LoadingState message="Cargando administradores" />
        ) : isError ? (
          <ErrorState message="No fue posible cargar los administradores" onRetry={() => void refetch()} />
        ) : lista.length === 0 ? (
          <EmptyState
            title="Aún no hay administradores"
            description={textoSegunAcceso(acceso, {
              gestion: 'Invita al contacto de la inmobiliaria para que consulte su información.',
              consulta: 'Todavía no hay administradores registrados en tu inmobiliaria.',
            })}
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border/70 shadow-elegant">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Administrador</th>
                  <th className="px-4 py-3 text-left">Contacto</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((admin) => (
                  <tr key={admin.id} className="border-t border-border/60 transition-colors hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{admin.nombre}</p>
                      <p className="text-xs text-muted-foreground">{admin.correo}</p>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">{admin.celular}</td>
                    <td className="px-4 py-3">
                      {/* «Invitado»: aún no ha entrado (no ha definido su contraseña por el enlace). */}
                      <Badge variant={!admin.activo ? 'secondary' : admin.ultimoLoginEn ? 'success' : 'info'}>
                        {admin.activo ? (admin.ultimoLoginEn ? 'Activo' : 'Invitado') : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {puedeEditar && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Reenviar invitación a ${admin.nombre}`}
                                onClick={() =>
                                  reenviar.mutate(admin.id, {
                                    onSuccess: () => setFeedback(`Invitación reenviada a ${admin.nombre}.`),
                                  })
                                }
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Reenviar invitación</TooltipContent>
                          </Tooltip>
                        )}
                        {(admin.activo ? acceso.desactivar : acceso.editar) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`${admin.activo ? 'Desactivar' : 'Activar'} ${admin.nombre}`}
                                onClick={() => setToToggle(admin)}
                              >
                                {admin.activo ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{admin.activo ? 'Desactivar' : 'Activar'}</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AdminFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          onCreated={(nombre) => setFeedback(`Invitación enviada a ${nombre}.`)}
        />

        <ConfirmDialog
          open={Boolean(toToggle)}
          onOpenChange={(open) => {
            if (!open) setToToggle(null);
          }}
          title={toToggle?.activo ? 'Desactivar administrador' : 'Activar administrador'}
          description={
            toToggle
              ? `${toToggle.nombre} ${toToggle.activo ? 'no podrá iniciar sesión' : 'podrá iniciar sesión de nuevo'}.`
              : undefined
          }
          confirmText={toToggle?.activo ? 'Desactivar' : 'Activar'}
          variant={toToggle?.activo ? 'destructive' : 'default'}
          loading={toggle.isPending}
          onConfirm={() => {
            if (!toToggle) return;
            toggle.mutate(
              { id: toToggle.id, activo: !toToggle.activo },
              {
                onSuccess: () => {
                  setFeedback(`Administrador ${toToggle.activo ? 'desactivado' : 'activado'} correctamente.`);
                  setToToggle(null);
                },
              },
            );
          }}
        />
      </div>
    </TooltipProvider>
  );
}
